import type { TranslationKey } from '../i18n';
import {
  CONFIDENCE_REQUIRED_STAGES,
  STAGE_ORDER,
  nextStageFor,
  routeStages,
  type Confidence,
  type Inquiry,
  type InteractionMode,
  type ProductType,
  type Stage,
} from '../types/api';

/** Board columns a card can be dropped on: the four stages + terminal ones. */
export type BoardColumnKey = Stage | 'WON' | 'LOST';

export interface DndTransitionInput {
  type: 'ADVANCE' | 'DROP' | 'WIN';
  confidence?: Confidence;
  reason?: string;
  /** Work mode — required when advancing PRIORITIZATION → REALIZATION. */
  interactionMode?: InteractionMode;
  /** ISO date-time — optional on-site audit date (AUDIT only). */
  auditAt?: string;
  /** Concept description — required when advancing REALIZATION →
   *  INTEGRATOR_SELECTION unless a CONCEPT artifact is attached. */
  conceptSummary?: string;
  conceptApprovedByClient?: boolean;
  /** Product line — chosen on the VALIDATION → VERIFICATION advance. */
  productType?: ProductType;
  /** Explicit ADVANCE target (educational VERIFICATION → TKP skip). */
  toStage?: Stage;
}

export type DropResolution =
  /** Same column — card snaps back silently, no toast, no request. */
  | { kind: 'ignore' }
  /** Drop not allowed by the pipeline rules — snap back + explanatory toast. */
  | { kind: 'invalid'; reasonKey: TranslationKey }
  /** Allowed without extra input — fire the transition immediately. */
  | { kind: 'submit'; input: DndTransitionInput }
  /** Allowed but needs reason and/or confidence — open the follow-up dialog. */
  | { kind: 'dialog'; action: 'ADVANCE' | 'DROP' | 'WIN'; toStage?: Stage };

/**
 * Maps a drag & drop gesture onto the transitions API contract.
 * The server only supports: ADVANCE one stage forward, DROP (reason),
 * WIN from REALIZATION or TKP. Backward/skip-ahead moves are rejected here with
 * an honest explanation instead of a request that would 4xx.
 */
export function resolveDrop(
  inquiry: Inquiry,
  target: BoardColumnKey,
  isManufacturer: boolean,
): DropResolution {
  if (!isManufacturer) return { kind: 'invalid', reasonKey: 'inquiries.dndErrRole' };
  if (inquiry.outcome !== 'NONE') return { kind: 'ignore' };

  if (target === 'LOST') return { kind: 'dialog', action: 'DROP' };

  if (target === 'WON') {
    return inquiry.stage === 'REALIZATION' || inquiry.stage === 'TKP'
      ? { kind: 'dialog', action: 'WIN' }
      : { kind: 'invalid', reasonKey: 'inquiries.dndErrWinStage' };
  }

  if (target === inquiry.stage) return { kind: 'ignore' };

  // Off-route columns are never valid targets for this product type
  // (educational deals skip PRIORITIZATION and INTEGRATOR_SELECTION).
  if (!routeStages(inquiry.productType).includes(target)) {
    return { kind: 'invalid', reasonKey: 'inquiries.dndErrOffRoute' };
  }

  const next = nextStageFor(inquiry.stage, inquiry.productType);
  if (target === next) {
    const needsConfidence =
      CONFIDENCE_REQUIRED_STAGES.includes(target) && !inquiry.confidence;
    // VALIDATION → VERIFICATION asks for the product line; «Опция возможностей»
    // → «Концепт проекта» for the work mode; «Концепт проекта» → next for the concept.
    const needsInput =
      (inquiry.stage === 'VALIDATION' && !inquiry.productType) ||
      inquiry.stage === 'PRIORITIZATION' ||
      inquiry.stage === 'REALIZATION';
    return needsConfidence || needsInput
      ? { kind: 'dialog', action: 'ADVANCE' }
      : { kind: 'submit', input: { type: 'ADVANCE' } };
  }

  // Educational fork «Нет, перейти к ТКП» — the drop target is the answer.
  if (
    inquiry.productType === 'EDUCATIONAL_ROBOT' &&
    inquiry.stage === 'VERIFICATION' &&
    target === 'TKP'
  ) {
    const needsConfidence =
      CONFIDENCE_REQUIRED_STAGES.includes('TKP') && !inquiry.confidence;
    return needsConfidence
      ? { kind: 'dialog', action: 'ADVANCE', toStage: 'TKP' }
      : { kind: 'submit', input: { type: 'ADVANCE', toStage: 'TKP' } };
  }

  const backwards =
    STAGE_ORDER.indexOf(target) < STAGE_ORDER.indexOf(inquiry.stage);
  return backwards
    ? { kind: 'invalid', reasonKey: 'inquiries.dndErrBackward' }
    : { kind: 'invalid', reasonKey: 'inquiries.dndErrSkip' };
}

/** Is this column an allowed drop target for the card being dragged? */
export function isValidDropTarget(
  inquiry: Inquiry,
  target: BoardColumnKey,
  isManufacturer: boolean,
): boolean {
  const r = resolveDrop(inquiry, target, isManufacturer);
  return r.kind === 'submit' || r.kind === 'dialog';
}
